import { useRoute } from '#imports'
import { computed } from 'vue'

import type { ChatRequestPageContext } from '~/api/shopping-assistant/types'

export type EntryPoint = 'product_page' | 'search'

// Arbitrary upper bound on a deep-link-seeded message. Not enforced by the
// backend; chosen to keep seeded prompts short and the URL reasonable.
const SEEDED_MESSAGE_MAX_LENGTH = 300

/**
 * Manages the context a chat is started with.
 */
export function useShoppingAssistantContext() {
  const route = useRoute()

  const pageContext = computed<ChatRequestPageContext | null>(() => {
    const productId = route.query.product_id

    if (typeof productId === 'string') {
      return {
        type: 'product_page',
        product_id: productId,
      }
    }

    return null
  })

  const entryPoint = computed<EntryPoint>(() =>
    pageContext.value?.type === 'product_page' ? 'product_page' : 'search',
  )

  // Initial message to auto-send on a seeded launch, trimmed and capped.
  // Empty string when absent (or whitespace-only) — no message is sent.
  const seededMessage = computed<string>(() => {
    const { message } = route.query

    if (typeof message !== 'string') {
      return ''
    }

    return message.trim().slice(0, SEEDED_MESSAGE_MAX_LENGTH)
  })

  // A seeded launch opens a brand-new chat pre-supplied with page context
  // and/or an initial message (push notification, email, in-page CTA). It never
  // targets an existing chat: when the route carries a chatId, that wins and the
  // seeded query params are ignored.
  const isSeededLaunch = computed<boolean>(() => {
    if (typeof route.params.chatId === 'string') {
      return false
    }

    return pageContext.value !== null || seededMessage.value !== ''
  })

  return {
    pageContext,
    entryPoint,
    seededMessage,
    isSeededLaunch,
  }
}
